import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { storeProductsApi, storesApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, dataTableSearchProps, usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ProductSelect, StoreSelect } from '@/components/selects';
import type { StoreProductRequest, StoreProductResponse } from '@/types/storeProduct';
import type { StoreResponse } from '@/types/store';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: StoreProductRequest = {
  storeId: 0,
  productId: 0,
  sellingPrice: 0,
};

export function StoreProductsPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [storeId, setStoreId] = useState<number | ''>('');
  const [data, setData] = useState<StoreProductResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StoreProductResponse | null>(null);
  const [form, setForm] = useState<StoreProductRequest>(empty);
  const [saving, setSaving] = useState(false);

  const selectedStore = stores.find((s) => s.id === storeId);
  const currency = selectedStore?.currencyCode ?? 'INR';

  useEffect(() => {
    storesApi.list({ page: 0, size: 200 }).then((res) => setStores(res.content)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!storeId) {
      setData([]);
      pagination.applyPageResponse({ content: [], page: 0, size: pagination.size, totalElements: 0, totalPages: 0 });
      return;
    }
    setLoading(true);
    try {
      const res = await storeProductsApi.list({
        storeId,
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load store products');
    } finally {
      setLoading(false);
    }
  }, [storeId, pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    pagination.resetPage();
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<StoreProductResponse>[] = [
    { key: 'productName', header: 'Product' },
    { key: 'sku', header: 'SKU' },
    { key: 'globalBarcode', header: 'Barcode' },
    { key: 'storeBarcode', header: 'Store barcode' },
    {
      key: 'sellingPrice',
      header: 'Selling price',
      render: (r) => formatCurrency(r.sellingPrice, currency),
    },
    {
      key: 'mrp',
      header: 'MRP',
      render: (r) => (r.mrp != null ? formatCurrency(r.mrp, currency) : '—'),
    },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({
              storeId: r.storeId,
              productId: r.productId,
              storeBarcode: r.storeBarcode,
              sellingPrice: r.sellingPrice,
              purchasePrice: r.purchasePrice,
              mrp: r.mrp,
              active: r.active,
            });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    if (!form.storeId || !form.productId) {
      notify.error('Store and product are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) await storeProductsApi.update(editing.id, form);
      else await storeProductsApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Store Products"
        description="Link catalog products to a store with that store's selling price and optional barcode"
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Store Products' }]}
        actions={
          <Button
            disabled={!storeId}
            onClick={() => {
              setEditing(null);
              setForm({ ...empty, storeId: storeId as number });
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> Add pricing
          </Button>
        }
      />
      <div className="card mb-4 p-4 text-sm text-slate-600 dark:text-slate-400">
        <p className="font-medium text-slate-800 dark:text-slate-200">What is a store product?</p>
        <p className="mt-2">
          Your <strong>Products</strong> catalog is organization-wide (SKU, name, default price).
          A <strong>store product</strong> is the same item enabled for one store with that store&apos;s
          selling price, MRP, and optional store barcode (must be unique within that store). POS and
          inventory for that store use these values; scan checks store barcode before catalog barcode.
          values. Create the product under Products first, then add it here for each store.
        </p>
      </div>
      <div className="mb-4 max-w-xs">
        <StoreSelect
          label="Store"
          value={storeId === '' ? '' : String(storeId)}
          onChange={(v) => setStoreId(v ? Number(v) : '')}
          placeholder="Select store…"
        />
      </div>
      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        {...dataTableSearchProps(pagination)}
        {...dataTablePageProps(pagination)}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit store product' : 'New store product'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <ProductSelect
              label="Product *"
              value={form.productId ? String(form.productId) : ''}
              onProductChange={(product) => {
                setForm({
                  ...form,
                  productId: product?.id ?? 0,
                  sellingPrice: product?.sellingPrice ?? form.sellingPrice,
                  mrp: product?.mrp ?? form.mrp,
                });
              }}
              onChange={(id) => setForm({ ...form, productId: id ? Number(id) : 0 })}
            />
          </div>
          <Input
            label={`Selling price (${currency}) *`}
            type="number"
            value={String(form.sellingPrice)}
            onChange={(e) => setForm({ ...form, sellingPrice: Number(e.target.value) })}
          />
          <Input
            label={`MRP (${currency})`}
            type="number"
            value={form.mrp != null ? String(form.mrp) : ''}
            onChange={(e) => setForm({ ...form, mrp: e.target.value ? Number(e.target.value) : undefined })}
          />
          <div className="sm:col-span-2">
            <Input
              label="Store barcode (unique per store)"
              value={form.storeBarcode ?? ''}
              onChange={(e) => setForm({ ...form, storeBarcode: e.target.value })}
              placeholder="Optional — used for POS scan at this store"
            />
          </div>
          <p className="sm:col-span-2 text-xs text-slate-500 dark:text-slate-400">
            Each store barcode can only be used once in this store. Leave empty to scan by catalog
            barcode from Products instead.
          </p>
        </div>
      </Modal>
    </div>
  );
}
